import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Brain, MessageCircle } from "lucide-react";
import {
  useAdminBotChatConversationQuery,
  AdminBotChatMessagesDocument,
  type AdminBotChatMessagesQuery,
  type AdminBotChatMessagesQueryVariables,
} from "@/generated/graphql";
import { formatDateTime } from "@/lib/format";
import { deriveMessageDisplay } from "@/lib/bot-conversations/deriveMessageDisplay";
import {
  reverseToAscending,
  prependOlderPage,
} from "@/lib/bot-conversations/messagePaging";

const MESSAGES_PAGE_SIZE = 30;
const SCROLL_TOP_THRESHOLD = 80;

type ChatMessage = AdminBotChatMessagesQuery["adminBotChatMessages"]["data"][number];

type ScrollAction =
  | { type: "bottom" }
  | { type: "restore"; prevHeight: number; prevTop: number };

export function ConversationView({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [memoryOpen, setMemoryOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollActionRef = useRef<ScrollAction | null>(null);
  const loadingOlderRef = useRef(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nextOlderPage, setNextOlderPage] = useState(2);
  const [pageCount, setPageCount] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const hasMoreOlder = nextOlderPage <= pageCount;

  const { data: convoData, loading: convoLoading } =
    useAdminBotChatConversationQuery({
      variables: { id: conversationId },
    });
  const convo = convoData?.adminBotChatConversation;

  const fetchPage = useCallback(
    (page: number) =>
      client.query<AdminBotChatMessagesQuery, AdminBotChatMessagesQueryVariables>({
        query: AdminBotChatMessagesDocument,
        variables: { conversationId, page, pageSize: MESSAGES_PAGE_SIZE, order: "desc" },
        fetchPolicy: "no-cache",
      }),
    [client, conversationId],
  );

  // Initial load: fetch the newest page and scroll to the bottom (chat style).
  useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);
    setMessages([]);
    setNextOlderPage(2);
    setPageCount(1);
    loadingOlderRef.current = false;
    fetchPage(1)
      .then((res) => {
        if (cancelled) return;
        const resp = res.data?.adminBotChatMessages;
        setMessages(reverseToAscending(resp?.data ?? []));
        setPageCount(resp?.pageCount ?? 1);
        scrollActionRef.current = { type: "bottom" };
        setInitialLoading(false);
      })
      .catch(() => {
        if (!cancelled) setInitialLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, fetchPage]);

  // After messages change, anchor the scroll: jump to bottom on initial load,
  // preserve the viewport on prepend so it doesn't jump when older messages load.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const action = scrollActionRef.current;
    if (!el || !action) return;
    if (action.type === "bottom") {
      el.scrollTop = el.scrollHeight;
    } else {
      el.scrollTop = el.scrollHeight - action.prevHeight + action.prevTop;
    }
    scrollActionRef.current = null;
  }, [messages]);

  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || nextOlderPage > pageCount) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;
    try {
      const res = await fetchPage(nextOlderPage);
      const older = res.data?.adminBotChatMessages?.data ?? [];
      scrollActionRef.current = { type: "restore", prevHeight, prevTop };
      setMessages((prev) => prependOlderPage(prev, older));
      setNextOlderPage((p) => p + 1);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [fetchPage, nextOlderPage, pageCount]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el && el.scrollTop < SCROLL_TOP_THRESHOLD) {
      loadOlder();
    }
  }, [loadOlder]);

  if (convoLoading && !convo) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!convo) {
    return (
      <p className="text-muted-foreground py-8 text-center">
        {t("admin.botConversations.noConversations")}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            <Badge variant="outline" className="capitalize">{convo.channel}</Badge>
            <span className="font-mono text-sm">{convo.channel_user_id}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">{t("admin.botConversations.columnUser")}</p>
              {convo.user ? (
                <Link to={`/admin/users/${convo.user.id}`} className="text-primary hover:underline">
                  {convo.user.name || convo.user.email}
                </Link>
              ) : (
                <Badge variant="outline" className="text-orange-500 border-orange-500">
                  {t("admin.botConversations.unlinked")}
                </Badge>
              )}
            </div>
            <div>
              <p className="text-muted-foreground">{t("admin.botConversations.firstSeen")}</p>
              <p>{convo.first_seen_at ? formatDateTime(convo.first_seen_at) : "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("admin.botConversations.lastSeen")}</p>
              <p>{convo.last_seen_at ? formatDateTime(convo.last_seen_at) : "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("admin.botConversations.totalMessages")}</p>
              <p className="inline-flex items-center gap-2">
                {convo.message_count ?? 0}
                {(convo.error_count ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-500 text-xs">
                    <AlertTriangle className="w-3.5 h-3.5" /> {convo.error_count}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMemoryOpen((o) => !o)}
              className="gap-2 px-0"
            >
              <Brain className="w-4 h-4" />
              {t("admin.botConversations.memorySummary")}
            </Button>
            {memoryOpen && (
              <div className="mt-2 rounded-md border bg-muted/30 p-3 text-sm">
                {convo.memory_summary ? (
                  <>
                    <p className="whitespace-pre-wrap">{convo.memory_summary}</p>
                    {convo.memory_updated_at && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {t("admin.botConversations.memoryUpdatedAt")}: {formatDateTime(convo.memory_updated_at)}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">{t("admin.botConversations.noMemory")}</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {initialLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : messages.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              {t("admin.botConversations.noConversations")}
            </p>
          ) : (
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex flex-col gap-3 overflow-y-auto max-h-[65vh] pr-1"
            >
              {loadingOlder && (
                <div className="flex justify-center py-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                </div>
              )}
              {!hasMoreOlder && !loadingOlder && (
                <p className="py-2 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                  {t("admin.botConversations.startOfConversation")}
                </p>
              )}
              {messages.map((m) => {
                const d = deriveMessageDisplay(m);
                return (
                  <div key={m.id} className={`flex ${d.side === "bot" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                        d.isError
                          ? "bg-red-500/10 border border-red-500/40"
                          : d.side === "bot"
                          ? "bg-primary/10"
                          : "bg-muted"
                      }`}
                    >
                      <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                        <span>
                          {d.side === "bot"
                            ? t("admin.botConversations.outbound")
                            : t("admin.botConversations.inbound")}
                        </span>
                        <span>{formatDateTime(m.createdAt)}</span>
                      </div>
                      {d.isMediaOnly ? (
                        <p className="italic text-muted-foreground">
                          {t("admin.botConversations.mediaOnly")}
                        </p>
                      ) : (
                        <p className="whitespace-pre-wrap">{d.text}</p>
                      )}
                      {d.badges.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {d.badges.map((b, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px]">
                              {t(`admin.botConversations.${b.kind}`)}: {b.value}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {d.isError && (
                        <p className="mt-1.5 flex items-start gap-1 text-xs text-red-500">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          {m.error}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
